// Non-blocking level-up card picker. CRITICAL: this modal does NOT
// swallow keyboard events for movement — WASD keeps feeding the
// controller while the cards are visible, so the player can still
// dodge NPC chase damage while choosing. See feedback memory
// `feedback_levelup_no_movement_block.md`.
//
// Stacks: if multiple levels come in back-to-back, we queue them
// internally and show the next roll as soon as the player picks.

import type { Powerup } from '../match/powerups';
import { RARITY_COLOR } from '../match/powerups';

type PendingRoll = {
  level: number;
  cards: Powerup[];
};

export class LevelUpModal {
  private root: HTMLDivElement;
  private queue: PendingRoll[] = [];
  private current: PendingRoll | null = null;
  private onPick: (p: Powerup) => void = () => {};
  private hotkeyHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'ui';
    // pointer-events: none so the underlying canvas still gets input;
    // only the card buttons themselves receive clicks. This is what
    // lets WASD keep feeding the controller.
    this.root.style.cssText = `
      left: 50%; bottom: 16%;
      transform: translateX(-50%);
      display: none;
      pointer-events: none;
      z-index: 40;
    `;
    parent.appendChild(this.root);
  }

  setOnPick(handler: (p: Powerup) => void) {
    this.onPick = handler;
  }

  enqueue(level: number, cards: Powerup[]) {
    if (cards.length === 0) return;
    this.queue.push({ level, cards });
    if (!this.current) this.showNext();
  }

  isOpen(): boolean {
    return this.current !== null;
  }

  hide() {
    this.root.style.display = 'none';
    this.root.innerHTML = '';
    if (this.hotkeyHandler) {
      removeEventListener('keydown', this.hotkeyHandler);
      this.hotkeyHandler = null;
    }
    this.current = null;
  }

  reset() {
    this.queue = [];
    this.hide();
  }

  private showNext() {
    const next = this.queue.shift();
    if (!next) { this.hide(); return; }
    this.current = next;
    this.render(next);
    this.bindHotkeys();
  }

  private pick(p: Powerup) {
    this.onPick(p);
    // small delay so the click animation reads
    this.hide();
    if (this.queue.length > 0) {
      setTimeout(() => this.showNext(), 80);
    }
  }

  private bindHotkeys() {
    this.hotkeyHandler = (e: KeyboardEvent) => {
      if (!this.current) return;
      const idx = e.code === 'Digit1' ? 0 : e.code === 'Digit2' ? 1 : e.code === 'Digit3' ? 2 : -1;
      if (idx < 0) return;
      const card = this.current.cards[idx];
      if (!card) return;
      e.preventDefault();
      this.pick(card);
    };
    addEventListener('keydown', this.hotkeyHandler);
  }

  private render({ level, cards }: PendingRoll) {
    this.root.style.display = 'block';
    const header = `
      <div style="
        text-align:center;
        margin-bottom:10px;
        font: 800 14px system-ui;
        letter-spacing:2px;
        color:#fde047;
        text-shadow: 0 2px 8px rgba(0,0,0,.8);
        pointer-events: none;
      ">LEVEL ${level} — PICK ONE</div>
    `;
    const cardMarkup = cards.map((c, i) => cardHtml(c, i)).join('');
    this.root.innerHTML = `
      ${header}
      <div style="
        display:flex;
        gap:12px;
        justify-content:center;
      ">${cardMarkup}</div>
    `;
    const buttons = this.root.querySelectorAll<HTMLButtonElement>('button[data-idx]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const idx = Number(btn.dataset.idx);
        const p = cards[idx];
        if (p) this.pick(p);
      });
    });
  }
}

function cardHtml(p: Powerup, idx: number): string {
  const color = RARITY_COLOR[p.rarity];
  const rarityLabel = p.rarity.toUpperCase();
  const hotkey = (idx + 1).toString();
  return `
    <button data-idx="${idx}" style="
      pointer-events: auto;
      width: 170px;
      min-height: 180px;
      background: rgba(10,12,18,.92);
      border: 2px solid ${color};
      border-radius: 14px;
      box-shadow: 0 8px 24px rgba(0,0,0,.55), 0 0 24px ${color}33;
      padding: 14px 12px 16px;
      font: 600 12px system-ui;
      color: #e5e7eb;
      text-align: center;
      cursor: pointer;
      transform: translateY(0);
      transition: transform .12s ease, box-shadow .12s ease;
    " onmouseover="this.style.transform='translateY(-4px)';this.style.boxShadow='0 14px 32px rgba(0,0,0,.65),0 0 36px ${color}66';"
       onmouseout="this.style.transform='translateY(0)';this.style.boxShadow='0 8px 24px rgba(0,0,0,.55),0 0 24px ${color}33';">
      <div style="
        font: 800 10px system-ui;
        letter-spacing: 2px;
        color: ${color};
        margin-bottom: 8px;
        text-shadow: 0 0 8px ${color}88;
      ">${rarityLabel}</div>
      <div style="
        font: 900 18px system-ui;
        color: #fafafa;
        margin-bottom: 10px;
        line-height: 1.1;
      ">${escapeHtml(p.name)}</div>
      <div style="
        font: 500 12px system-ui;
        color: #cbd5e1;
        opacity: .85;
        margin-bottom: 14px;
        min-height: 32px;
      ">${escapeHtml(p.blurb)}</div>
      <div style="
        display: inline-block;
        padding: 2px 8px;
        border-radius: 10px;
        background: ${color}22;
        border: 1px solid ${color}66;
        font: 700 10px system-ui;
        color: ${color};
      ">[${hotkey}]</div>
    </button>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '"' ? '&quot;' : '&#39;'
  ));
}

